import { z } from 'zod';
// The `mobile` build carries the patterns that tell a mobile from a landline.
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';
/**
 * Form-field rules, shared by the API, the admin panel and the marketing site.
 *
 * These live in one place because a rule enforced only in the browser is not a
 * rule — the server has to hold the same line, and the two drifting apart is
 * how "the form accepted it but the API rejected it" bugs happen.
 *
 * The client layer on top of these does two extra things the schemas cannot:
 * it *prevents* the keystroke (so a mobile-number field never shows a letter at
 * all) and it caps length with `maxLength` (so the browser stops typing rather
 * than showing an error after the fact). Rejecting after the fact is a worse
 * experience than not accepting the character.
 */
// ── Limits ──────────────────────────────────────────────────────────────────
export const FIELD_LIMITS = {
    /** E.164 allows 15 digits at most, and the `+` sits outside that count. */
    mobile: 16,
    /** Digits only, ignoring a leading `+`. */
    mobileDigits: 15,
    email: 60,
    name: 60,
};
// ── Mobile number ───────────────────────────────────────────────────────────
/**
 * Characters a mobile-number field may contain: digits, and a `+` that may only
 * lead. Everything else — spaces, hyphens, brackets, letters — is refused at
 * the keystroke, because a number a human "formatted" is a number that fails
 * an OTP send later.
 */
export const MOBILE_ALLOWED_PATTERN = /^\+?\d*$/;
/**
 * Strips a mobile-number input down to what the field accepts.
 *
 * Used as an `onChange` filter, so pasting `+91 (98765) 43210` yields
 * `+919876543210` rather than an error the user has to fix by hand.
 */
export function sanitiseMobileInput(value) {
    const leadingPlus = value.trimStart().startsWith('+');
    const digits = value.replace(/\D/g, '').slice(0, FIELD_LIMITS.mobileDigits);
    return leadingPlus ? `+${digits}` : digits;
}
/** True when a keystroke may be accepted into a mobile-number field. */
export function isAllowedMobileKey(current, key) {
    if (key.length !== 1)
        return true; // Backspace, arrows, Tab, …
    if (key === '+')
        return current.length === 0;
    if (!/\d/.test(key))
        return false;
    return current.replace(/\D/g, '').length < FIELD_LIMITS.mobileDigits;
}
/**
 * A mobile number as typed. Length is bounded to E.164's 15 digits.
 *
 * Deliberately looser than {@link ReachableMobileSchema}: a society may hold an
 * emergency contact abroad, and refusing it would be wrong. Where a number has
 * to receive an Indian OTP, use the strict schema instead.
 */
export const MobileSchema = z
    .string()
    .trim()
    .min(1, 'Enter a mobile no.')
    .max(FIELD_LIMITS.mobile, `A mobile no can be at most ${FIELD_LIMITS.mobileDigits} digits.`)
    .regex(MOBILE_ALLOWED_PATTERN, 'A mobile no can contain only digits and a leading +.')
    .refine((value) => value.replace(/\D/g, '').length >= 8, 'That mobile no is too short.')
    .refine((value) => value.replace(/\D/g, '').length <= FIELD_LIMITS.mobileDigits, `A mobile no can be at most ${FIELD_LIMITS.mobileDigits} digits.`);
/**
 * A mobile number an OTP can reach, in any country, normalised to E.164.
 *
 * `defaultCountry` is used only when the value carries no `+` prefix — the
 * society's country, so a resident typing their own number the way they say
 * it aloud still lands on the right country code.
 */
export function mobileSchemaFor(defaultCountry = 'IN') {
    return z
        .string()
        .trim()
        .transform((value, ctx) => {
        const parsed = parsePhoneNumberFromString(value, defaultCountry);
        if (!parsed || !parsed.isValid()) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: mobileProblem(value, parsed?.country ?? defaultCountry) });
            return z.NEVER;
        }
        return parsed.number;
    });
}
/**
 * Says what is wrong with a number in words a person can act on. "Enter a
 * valid number" sends someone who typed +91 back to check the +91.
 */
export function mobileProblem(value, country) {
    const digits = value.replace(/\D/g, '');
    if (digits.length < 6)
        return 'Enter a mobile number with its country code.';
    const rule = MOBILE_RULES[country];
    const where = countryName(country);
    return rule
        ? `That is not a valid mobile number for ${where} — it needs ${rule}. Landlines and short codes cannot receive an OTP.`
        : `That is not a valid mobile number for ${where} — landlines and short codes cannot receive an OTP.`;
}
/** The shape of a mobile number in the countries most residents are in. */
const MOBILE_RULES = {
    IN: '10 digits starting with 6, 7, 8 or 9',
    AE: '9 digits starting with 5',
    US: '10 digits',
    GB: '10 digits starting with 7',
    SG: '8 digits starting with 8 or 9',
    NP: '10 digits starting with 9',
    BD: '10 digits starting with 1',
    LK: '9 digits starting with 7',
};
function countryName(code) {
    try {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code;
    }
    catch {
        return code;
    }
}
/** The default-country form, for callers that have no society in hand. */
export const ReachableMobileSchema = mobileSchemaFor('IN');
/** @deprecated Kept for older callers; identical to {@link ReachableMobileSchema}. */
export const IndianMobileSchema = ReachableMobileSchema;
// ── Email ───────────────────────────────────────────────────────────────────
/**
 * Email, capped at 60 characters.
 *
 * The cap is a product decision, not a standard: RFC 5321 allows 254. Sixty
 * covers every real address we have seen and keeps the column, the CSV export
 * and the table cell all comfortable.
 */
export const EmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Enter an email address.')
    .max(FIELD_LIMITS.email, `An email address can be at most ${FIELD_LIMITS.email} characters.`)
    // Zod's `.email()` accepts a few shapes a mail server will not, so the
    // structural check is spelled out: a local part, one @, a dotted domain, and
    // a TLD of at least two letters.
    .regex(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/, 'Enter a valid email address.')
    .refine((value) => !value.includes('..'), 'Enter a valid email address.');
export const OptionalEmailSchema = z
    .union([z.literal(''), EmailSchema])
    .transform((value) => (value === '' ? null : value));
// ── Person name ─────────────────────────────────────────────────────────────
/**
 * Characters a name may contain.
 *
 * Unicode letters and marks, because residents are named रवि शर्मा and
 * D'Souza and Fernandes-Pereira. Digits are excluded — "Flat 101" is not a
 * name, and a numeric name is nearly always a paste into the wrong field.
 */
export const NAME_ALLOWED_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M}\s.'’-]*$/u;
/** Collapses runs of whitespace so "Ravi   Sharma" stores as "Ravi Sharma". */
export function sanitiseNameInput(value) {
    return value.replace(/\s{2,}/g, ' ').slice(0, FIELD_LIMITS.name);
}
export function isAllowedNameKey(current, key) {
    if (key.length !== 1)
        return true;
    if (current.length >= FIELD_LIMITS.name)
        return false;
    return /[\p{L}\p{M}\s.'’-]/u.test(key);
}
export const NameSchema = z
    .string()
    .trim()
    .min(2, 'Enter at least 2 characters.')
    .max(FIELD_LIMITS.name, `A name can be at most ${FIELD_LIMITS.name} characters.`)
    .regex(NAME_ALLOWED_PATTERN, 'A name can contain only letters, spaces, apostrophes and hyphens.');
export const FullNameSchema = NameSchema.refine((value) => value.trim().length >= 2, 'Enter a full name.');
// ── Money and durations ─────────────────────────────────────────────────────
/**
 * Characters a positive-number field may contain: digits and at most one dot.
 * A minus sign is refused at the keystroke — a price of −₹500 is never what
 * someone meant to type, and catching it on submit is too late.
 */
export function sanitisePositiveNumberInput(value, decimals = 2) {
    const cleaned = value.replace(/[^\d.]/g, '');
    const [whole = '', ...rest] = cleaned.split('.');
    if (rest.length === 0)
        return whole;
    return `${whole}.${rest.join('').slice(0, decimals)}`;
}
export function isAllowedPositiveNumberKey(current, key, decimals = 2) {
    if (key.length !== 1)
        return true;
    if (key === '.')
        return decimals > 0 && !current.includes('.');
    if (!/\d/.test(key))
        return false;
    const dot = current.indexOf('.');
    if (dot === -1)
        return true;
    return current.length - dot - 1 < decimals;
}
/**
 * A price in rupees as typed into a form, converted to integer paise.
 *
 * Rupees never survive past this boundary — everything downstream is paise
 * (integer paise, never a float). The string is parsed digit-by-digit rather than through
 * `parseFloat`, because `parseFloat('4250.55') * 100` is 425054.99999999994.
 */
export const PricePaiseSchema = z
    .string()
    .trim()
    .min(1, 'Enter an amount.')
    .regex(/^\d+(\.\d{1,2})?$/, 'Enter a positive amount, with at most 2 decimal places.')
    .transform((value) => {
    const [whole = '0', fraction = ''] = value.split('.');
    return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
});
/** Same, but `0` is allowed — a free amenity, a waived charge. */
export const OptionalPricePaiseSchema = z
    .union([z.literal(''), PricePaiseSchema])
    .transform((value) => (value === '' ? null : value));
/** A whole positive count: days, months, hours, quantity. */
export const PositiveIntSchema = z.coerce
    .number()
    .int('Enter a whole number.')
    .positive('Enter a number greater than zero.');
/** Same, allowing zero — "0 days grace" is a real setting. */
export const NonNegativeIntSchema = z.coerce
    .number()
    .int('Enter a whole number.')
    .min(0, 'This cannot be negative.');
/** A duration in minutes, bounded so a booking cannot run for a year. */
export const DurationMinutesSchema = PositiveIntSchema.max(60 * 24 * 30, 'A duration cannot exceed 30 days.');
// ── File uploads ────────────────────────────────────────────────────────────
/**
 * Image formats the platform accepts.
 *
 * SVG is included because logos arrive as SVG, but it is the one format here
 * that can carry script — it must be sanitised server-side before it is served,
 * and never served from the app's own origin.
 */
export const ALLOWED_IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'image/webp',
];
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.svg', '.webp'];
/** The `accept` attribute for a file input. Both lists — a browser may send either. */
export const IMAGE_ACCEPT_ATTRIBUTE = [
    ...ALLOWED_IMAGE_MIME_TYPES,
    ...ALLOWED_IMAGE_EXTENSIONS,
].join(',');
/**
 * Whether a file may be uploaded as an image.
 *
 * Checks the MIME type *and* the extension: a browser reports `application/
 * octet-stream` for some files, and a filename alone is trivially renamed. The
 * server re-checks the magic bytes — this is the client-side courtesy, not the
 * control.
 */
export function isAllowedImageFile(file) {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    const extensionOk = ALLOWED_IMAGE_EXTENSIONS.includes(extension);
    const typeOk = file.type === ''
        ? true // Some browsers omit it; the extension and the server decide.
        : ALLOWED_IMAGE_MIME_TYPES.includes(file.type);
    return extensionOk && typeOk;
}
export const IMAGE_REJECTED_MESSAGE = 'Choose a JPG, PNG, SVG or WEBP image.';
// ── Place ───────────────────────────────────────────────────────────────────
/** ISO 3166-1 alpha-2, upper-cased. The console picks it from a list. */
export const countryCodeSchema = z
    .string()
    .trim()
    .length(2, 'A two-letter country code.')
    .transform((value) => value.toUpperCase());
/**
 * An IANA zone name. Checked against the runtime's own table rather than a
 * list of our own, so a zone the platform can format is a zone it accepts —
 * and one it cannot is refused before it reaches a column.
 */
export const timezoneSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine((value) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Not a known timezone.' });
//# sourceMappingURL=fields.js.map